import { useEffect, useState } from 'react';
import { useFutureOrderStore } from '../../../../components/Stores/FutureOrdersStore';
import { useAccountStore } from '../../../../components/Stores/accountStore';

import React from 'react';
import { useMaterialsStore } from '../../../../components/Stores/MaterialsStore';
import { DateValueInputWithDateSwitch } from '../../../../components/UI-Elements/Charts/DateValueInputMultiChart/DateValueInputWithDateSwitch';
import { createFutureOrder } from '../../../../sp-firebase/futureOrderFB';
import { ISupplierExposure } from '../../../../sp-types/FutureOrderType';
import { Product } from '../../../../sp-types/productType';
import { getHighestAbsoluteValue } from '../../../../utils/MonthYear/MonthYearValue';
import { LoadingLogoComponent } from '../../../Quotes/LoadingLogoComponent';
import { updateEditedFutureOrderCharts } from '../CreateOrderSummary/OrderSummaryUtils';
import {
  getMaterialExposureCharts,
  getStartingDate,
  multiplyQuantityBy,
} from '../FOIndexedClientExposure/FillOrderAdjustmentsUtils';
import { EditFutureOrderTable } from './GraphsTables/ProductMaterialTable';
import { FOIndexedRawMaterialsButtonSection } from './RawMaterialBarChart/RawMaterialExposureButtonSection';
import { Timestamp } from 'firebase/firestore';
import { getLastDate } from '../AddNewFutureOrderUtils';
import { useCurrencyRates } from '@hooks/useCurrencyRates';

interface Props {
  backAction: () => void;
  nextAction: () => void;
  products: Product[];
}

export const FOIndexedSupplierExposure: React.FC<Props> = ({ backAction, nextAction, products }) => {
  const { editedFutureOrder, updateSingleSupplierExposureCalendar, updateSupplierExposure, setEditedFutureOrder } =
    useFutureOrderStore();
  const { orderDetails } = editedFutureOrder;
  const { account, isAccountLoading, formulaColorDictionary: colorsMap } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const { pricesMap } = useCurrencyRates();
  const [isButtonLoading, setIsButtonsLoading] = useState(false);

  useEffect(() => {
    const updatedOrder = updateEditedFutureOrderCharts(editedFutureOrder, products, materialsMap);

    setEditedFutureOrder(updatedOrder);
  }, []);

  return isAccountLoading ? (
    <LoadingLogoComponent />
  ) : (
    <>
      <div className="flex flex-1 flex-col">
        <div className="flex flex-col w-full">
          <EditFutureOrderTable
            materialsMap={materialsMap}
            futureOrderData={{
              editedFutureOrder: editedFutureOrder,
              updateSingleSupplierExposureCalendar,
              updateSupplierExposure,
            }}
          />
        </div>
        <div className="flex flex-col flex-1 py-1">
          <hr className="w-full" />
        </div>
        <div>
          <DateValueInputWithDateSwitch
            mainChart={{
              data: multiplyQuantityBy(orderDetails.productDeliveryCalendar || [], orderDetails.pricePerUnit || 1),
              title: 'Product price',
              type: 'bar',
              transparent: true,
              isFlipped: true,
              barThickness: 10,
            }}
            isHideValueInput={true}
            numberOfMonthsToShow={24}
            changeMonths={12}
            additionalCharts={getMaterialExposureCharts(
              editedFutureOrder.supplierExposure.map((item) => ({
                ...item,
                exposureCalendar: item.exposureCalendar.map((dateValue) => ({
                  ...dateValue,
                  value: pricesMap[item.formulaId] * dateValue.value,
                })),
              })),
              materialsMap,
              colorsMap,
            )}
            startingDate={getStartingDate()}
            endDate={orderDetails.productDeliveryCalendar && getLastDate(orderDetails.productDeliveryCalendar)}
            suggestedMaxValue={
              orderDetails.productDeliveryCalendar &&
              getHighestAbsoluteValue(orderDetails.productDeliveryCalendar) * 1.2
            }
            suggestedMinValue={
              orderDetails.productDeliveryCalendar &&
              -getHighestAbsoluteValue(orderDetails.productDeliveryCalendar) * 1.2
            }
          />
        </div>
      </div>
      <div className="flex sticky bottom-0 bg-white pt-2">
        <FOIndexedRawMaterialsButtonSection
          backAction={backAction}
          nextAction={() => {
            account.id &&
              editedFutureOrder.uuid &&
              SaveRawMaterialExposureAndRoute(
                setIsButtonsLoading,
                editedFutureOrder.uuid,
                editedFutureOrder.supplierExposure,
                account.id,
                nextAction,
              );
          }}
          isButtonLoading={isButtonLoading}
        />
      </div>
    </>
  );
};

const SaveRawMaterialExposureAndRoute = async (
  setIsLoading: (_value: boolean) => void,
  futureOrderUuid: string,
  supplierExposure: ISupplierExposure[],
  accountId: string,
  routeFunc: (_newId?: string) => void,
) => {
  setIsLoading(true);
  await createFutureOrder(accountId, {
    status: 'underwriting',
    updatedAt: Timestamp.now(),
    uuid: futureOrderUuid,
    supplierExposure: supplierExposure,
  });
  setIsLoading(false);
  routeFunc();
};
